/**
 * Super-admin Users list: server-side sort and filters for GET /admin/users/list.
 * Mirrors backend app/schemas/users/admin_users.py.
 */
import type { UserRole } from './index';

export type AdminUserSortField = 'email' | 'name' | 'created_at' | 'organization' | 'role' | 'profile';

export type SortOrder = 'asc' | 'desc';

export interface AdminUserSort {
  field: AdminUserSortField;
  order: SortOrder;
}

/** Query params of GET /admin/users/list. Everything except skip/limit is optional. */
export interface AdminUserListParams {
  skip: number;
  limit: number;
  sort_by?: AdminUserSortField;
  sort_order?: SortOrder;
  organization_id?: string;
  profile_id?: string;
  /** Only users with no assigned profile (they run on their role's default). */
  without_profile?: boolean;
  role?: UserRole;
  /** Inclusive lower bound, ISO datetime with offset. */
  created_from?: string;
  /** Exclusive upper bound, ISO datetime with offset. */
  created_before?: string;
}

/** Profile filter value for users with no assigned profile (role default). */
export const DEFAULT_PROFILE_FILTER = '__default__';

/**
 * True for a complete <input type="date"> value with a plausible year. Chrome emits
 * partial years (0002-…, 0020-…) while typing, and years past 9999 can't be sent.
 */
export function isCompleteDateInput(value: string): boolean {
  const match = /^(\d{4})-\d{2}-\d{2}$/.exec(value);
  return !!match && Number(match[1]) >= 1900;
}

/** Filter bar state. Empty string means "no filter"; dates are <input type="date"> values (YYYY-MM-DD). */
export interface AdminUserFilters {
  organizationId: string;
  profileId: string;
  role: UserRole | '';
  createdFrom: string;
  createdTo: string;
}
